import { isTransportOrder, type Order } from '#domain/Order.ts';

import type { TourStop } from './TourStop.ts';
import { type CapacityMetric, normalizeWeightKg } from './tourCapacity.ts';

export type StopLoadMetric = {
    total: number;
    delta: number;
    changed: boolean;
    deviation?: number;
};

export type MissingCargoData = 'weight' | 'loadingMeter' | 'quantity' | 'weightAndLoadingMeter';

export type StopLoadMetrics = {
    weight?: StopLoadMetric;
    loadingMeter?: StopLoadMetric;
    liter?: StopLoadMetric;
    cubicMeter?: StopLoadMetric;
    valueState: 'complete' | 'partial' | 'missing';
    missingCargoData?: MissingCargoData;
    exceededCapacity?: CapacityMetric[];
};

type MetricKey = CapacityMetric;
export type CapacityLimits = Partial<Record<CapacityMetric, number>>;
type MaterialValue = { target?: number; loaded?: number; unloaded?: number };
type MaterialComponent = {
    key: string;
    metric: MetricKey;
    value: MaterialValue;
    factor: number;
};
type MetricChange = {
    delta: number;
    deviation: number;
    changed: boolean;
};
type MaterialChange = {
    nextBalance: number;
    delta: number;
    deviation: number;
};
const metricKeys: MetricKey[] = ['weight', 'loadingMeter', 'liter', 'cubicMeter'];

const createMetricRecord = <Value>(factory: () => Value): Record<MetricKey, Value> => ({
    weight: factory(),
    loadingMeter: factory(),
    liter: factory(),
    cubicMeter: factory(),
});

const normalizeMetricValue = (value: number) => Number(value.toFixed(2));

const getMissingCargoData = (missingWeight: boolean, missingLoadingMeter: boolean): MissingCargoData => {
    if (missingWeight && missingLoadingMeter) {
        return 'weightAndLoadingMeter';
    }
    return missingWeight ? 'weight' : 'loadingMeter';
};

const getValueState = (order: Order): Pick<StopLoadMetrics, 'valueState' | 'missingCargoData'> => {
    if (!isTransportOrder(order)) {
        return { valueState: 'complete' };
    }

    const cargoMaterials = order.materials?.filter(material => material.type === 'cargo-material') ?? [];
    const bulkMaterials = order.materials?.filter(material => material.type === 'bulk-material') ?? [];

    if (cargoMaterials.length === 0) {
        return { valueState: bulkMaterials.length > 0 ? 'complete' : 'missing' };
    }

    const hasCargoWeight = cargoMaterials.some(material => material.weight?.target !== undefined);
    const missingWeight = hasCargoWeight && cargoMaterials.some(material => material.weight?.target === undefined);
    const hasLoadingMeter = cargoMaterials.some(material => material.loadingMeter?.target !== undefined);
    const missingLoadingMeter =
        hasLoadingMeter && cargoMaterials.some(material => material.loadingMeter?.target === undefined);

    if (missingWeight || missingLoadingMeter) {
        return {
            valueState: 'partial',
            missingCargoData: getMissingCargoData(missingWeight, missingLoadingMeter),
        };
    }

    return { valueState: !hasCargoWeight && !hasLoadingMeter && bulkMaterials.length === 0 ? 'missing' : 'complete' };
};

const getMaterialComponents = (order: Order): MaterialComponent[] => {
    if (!isTransportOrder(order)) {
        return [];
    }

    const components: MaterialComponent[] = [];

    for (const material of order.materials ?? []) {
        const keyPrefix = `${order.id}:${material.id}`;
        if (material.type === 'cargo-material') {
            if (material.weight) {
                components.push({
                    key: `${keyPrefix}:weight`,
                    metric: 'weight',
                    value: material.weight,
                    factor: 1,
                });
            }
            if (material.loadingMeter) {
                components.push({
                    key: `${keyPrefix}:loadingMeter`,
                    metric: 'loadingMeter',
                    value: material.loadingMeter,
                    factor: 1,
                });
            }
            continue;
        }

        const metricByUnit = {
            kilogram: { metric: 'weight' as const, factor: 1 },
            ton: { metric: 'weight' as const, factor: 1000 },
            liter: { metric: 'liter' as const, factor: 1 },
            'cubic-meter': { metric: 'cubicMeter' as const, factor: 1 },
        };
        const definition = metricByUnit[material.unitType];

        components.push({
            key: `${keyPrefix}:${definition.metric}`,
            metric: definition.metric,
            value: material.quantity,
            factor: definition.factor,
        });
    }

    return components;
};

const getLoadAmount = (value: MaterialValue, factor: number) => {
    const amount = value.loaded ?? value.target;

    return amount === undefined ? undefined : amount * factor;
};

const getUnloadAmount = (value: MaterialValue, factor: number, balance: number | undefined): number | undefined => {
    if (value.unloaded !== undefined) {
        return value.unloaded * factor;
    }

    if (balance !== undefined) {
        return balance;
    }

    return value.target === undefined ? undefined : value.target * factor;
};

const getMaterialChange = (
    stopType: 'load' | 'unload',
    component: MaterialComponent,
    currentBalance: number | undefined
): MaterialChange | undefined => {
    if (stopType === 'load') {
        const amount = getLoadAmount(component.value, component.factor);

        if (amount === undefined) {
            return undefined;
        }

        return {
            nextBalance: (currentBalance ?? 0) + amount,
            delta: amount,
            deviation:
                component.value.loaded !== undefined && component.value.target !== undefined
                    ? amount - component.value.target * component.factor
                    : 0,
        };
    }

    const amount = getUnloadAmount(component.value, component.factor, currentBalance);

    if (amount === undefined) {
        return undefined;
    }

    if (currentBalance === undefined) {
        return {
            nextBalance: -amount,
            delta: -amount,
            deviation: 0,
        };
    }

    return {
        nextBalance: 0,
        delta: -amount,
        deviation: component.value.unloaded !== undefined ? amount - currentBalance : 0,
    };
};

const addCurrentTotals = (
    stopMetrics: StopLoadMetrics,
    balances: Record<MetricKey, Map<string, number>>,
    changes?: Record<MetricKey, MetricChange>
) => {
    for (const metric of metricKeys) {
        const change = changes?.[metric];
        const metricBalances = balances[metric];
        if (metricBalances.size === 0 && !change?.changed) {
            continue;
        }

        const total = [...metricBalances.values()].reduce((sum, balance) => sum + balance, 0);
        const delta = normalizeMetricValue(change?.delta ?? 0);
        const deviation = normalizeMetricValue(change?.deviation ?? 0);
        stopMetrics[metric] = {
            total: metric === 'weight' ? normalizeWeightKg(total) : normalizeMetricValue(total),
            delta,
            changed: change?.changed ?? false,
            ...(deviation !== 0 ? { deviation } : {}),
        };
    }
};

const getExceededCapacity = (metrics: StopLoadMetrics, limits: CapacityLimits): CapacityMetric[] =>
    metricKeys.filter(metric => {
        const limit = limits[metric];
        const value = metrics[metric]?.total;
        if (limit === undefined || value === undefined) {
            return false;
        }

        return value > (metric === 'weight' ? normalizeWeightKg(limit) : limit);
    });

export const getTourLoadMetrics = (
    stops: TourStop[],
    orders: Order[],
    capacityLimits: CapacityLimits = {}
): Map<string, StopLoadMetrics> => {
    const orderById = new Map(orders.map(order => [order.id, order]));
    const balances = createMetricRecord(() => new Map<string, number>());
    const result = new Map<string, StopLoadMetrics>();

    for (const stop of stops) {
        if (stop.type === 'execute') {
            const stopMetrics: StopLoadMetrics = { valueState: 'complete' };
            addCurrentTotals(stopMetrics, balances);
            stopMetrics.exceededCapacity = getExceededCapacity(stopMetrics, capacityLimits);
            if (metricKeys.some(metric => stopMetrics[metric])) {
                result.set(stop.id, stopMetrics);
            }
            continue;
        }

        if (stop.type !== 'load' && stop.type !== 'unload') {
            continue;
        }

        const order = orderById.get(stop.orderId);
        if (!order || !isTransportOrder(order)) {
            continue;
        }

        const changes = createMetricRecord<MetricChange>(() => ({ delta: 0, deviation: 0, changed: false }));

        for (const component of getMaterialComponents(order)) {
            const change = changes[component.metric];
            const metricBalances = balances[component.metric];
            const currentBalance = metricBalances.get(component.key);
            const materialChange = getMaterialChange(stop.type, component, currentBalance);

            if (!materialChange) {
                continue;
            }

            metricBalances.set(component.key, materialChange.nextBalance);
            change.delta += materialChange.delta;
            change.deviation += materialChange.deviation;
            change.changed = true;
        }

        const stopMetrics: StopLoadMetrics = { ...getValueState(order) };
        addCurrentTotals(stopMetrics, balances, changes);
        stopMetrics.exceededCapacity = getExceededCapacity(stopMetrics, capacityLimits);

        result.set(stop.id, stopMetrics);
    }

    return result;
};

export const hasExceededTourCapacity = (stops: TourStop[], orders: Order[], capacityLimits: CapacityLimits): boolean =>
    [...getTourLoadMetrics(stops, orders, capacityLimits).values()].some(
        metrics => (metrics.exceededCapacity?.length ?? 0) > 0
    );
